import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import { cancelSimulation } from '../../../actions'
import { Logger, LoggerTypes } from '../../../utils'
import { Button, buttonModes, Icon, iconNames } from '../../../components'

export const SimulationActionDialogCancel = forwardRef(
  ({ currentProject, currentScenario, simulation, openDialog, setOpenDialog }, ref) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()

    const onClickClose = (e) => {
      e.preventDefault()
      e.stopPropagation()

      Logger.trace(LoggerTypes.CONTROLLER, 'SimulationActionDialogCancel.onClickClose()')
      setOpenDialog(!openDialog)
    }

    const onClickCancel = (e) => {
      e.preventDefault()
      e.stopPropagation()

      const pid = currentProject?.id || null
      const sid = currentScenario?.id || null

      Logger.trace(LoggerTypes.CONTROLLER, 'SimulationActionDialogCancel.onCloseClick()', { simulation })

      dispatch(cancelSimulation(pid, sid, simulation))
        .then((res) => {
          Logger.trace(
            LoggerTypes.CONTROLLER,
            'SimulationActionDialogCancel.onClickCancel().dispatch(cancelSimulation(...)).then()',
            {
              simulation,
              res
            }
          )

          if (res?.data?.isAxiosError) {
            enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
          }
          setOpenDialog(!openDialog)
        })
        .catch((res) => {
          Logger.trace(
            LoggerTypes.WARN,
            'SimulationActionDialogCancel.onClickCancel().dispatch(cancelSimulation(...)).catch()',
            { res }
          )

          const error = (res && res.response && res.response.data && JSON.stringify(res.response.data)) || ''
          enqueueSnackbar(`${t('common.api_failure')} ${error}`, { variant: 'error' })
        })
    }

    Logger.trace(LoggerTypes.CONTAINER, 'SimulationActionDialogCancel.render()', {
      currentProject,
      currentScenario,
      openDialog,
      simulation
    })

    return (
      <Dialog ref={ref} open={openDialog} onClose={onClickClose} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">{t('simulations.dialogs.cancel.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('simulations.dialogs.cancel.description')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            mode={buttonModes.SECONDARY_ACTION}
            text={t('common.cancel')}
            icon={<CloseIcon />}
            onClick={onClickClose}
          />
          <Button
            mode={buttonModes.PRIMARY_ACTION}
            text={t('common.stop')}
            icon={<Icon name={iconNames.CIRCLE_CANCEL} size={14} />}
            onClick={onClickCancel}
          />
        </DialogActions>
      </Dialog>
    )
  }
)

SimulationActionDialogCancel.displayName = 'SimulationActionDialogCancel'

SimulationActionDialogCancel.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  simulation: PropTypes.object.isRequired,
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired
}

SimulationActionDialogCancel.defaultProps = {
  currentProject: null,
  currentScenario: null,
  simulation: {},
  openDialog: false,
  setOpenDialog: () => {}
}
